import React, { useEffect, useState } from 'react';
import { PageHeader, Card, Button, Alert } from '../components/ui';
import { settingsApi } from '../services/api';

// ---------------- TABS ----------------
const TABS = [
  { id: 'domo', label: 'Domo Credentials' },
  { id: 'pbi', label: 'Power BI Credentials' }
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('domo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ---------------- STATE ----------------
  const [domo, setDomo] = useState({
    clientId: '',
    clientSecret: '',
    host: '',
    accessToken: ''
  });

  const [pbi, setPbi] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
    defaultWorkspace: ''
  });

  // ---------------- SAVE DOMO ----------------
  const saveDomo = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await settingsApi.saveDomo(domo);

      setSuccess('Domo credentials saved successfully');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save Domo credentials');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- SAVE PBI ----------------
  const savePbi = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await settingsApi.savePbi(pbi);

      setSuccess('Power BI credentials saved successfully');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save Power BI credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const details = await settingsApi.getDetails();
        console.log('Fetched settings details:', details);

        // details.data.data can be null (e.g. no settings saved yet) —
        // fall back to an empty object so the form just renders blank
        // fields instead of throwing.
        const data = details.data?.data || {};

        setDomo({
          clientId: data.domo_client_id || '',
          clientSecret: data.domo_client_secret || '',
          host: data.domo_domain || '',
          accessToken: data.domo_access_token || ''
        });

        setPbi({
          tenantId: data.pbi_tenant_id || '',
          clientId: data.pbi_app_id || '',
          clientSecret: data.pbi_app_secret || '',
          defaultWorkspace: data.pbi_default_workspace || ''
        });
        
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to fetch settings');
      }
    };

    fetchSettings();
  }, []);

  return (
    <div className="animate-fade-in">

      {/* HEADER */}
      <PageHeader
        title="Settings"
        subtitle="Manage Domo & Power BI credentials"
      />

      {/* STATUS */}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* TABS */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* DOMO */}
      {activeTab === 'domo' && (
        <Card>
          <h3>Domo Credentials</h3>

          <Input
            label="Client ID"
            value={domo.clientId}
            onChange={(e) => setDomo({ ...domo, clientId: e.target.value })}
          />

          <Input
            label="Client Secret"
            type="password"
            value={domo.clientSecret}
            onChange={(e) => setDomo({ ...domo, clientSecret: e.target.value })}
          />

          <Input
            label="Host"
            value={domo.host}
            onChange={(e) => setDomo({ ...domo, host: e.target.value })}
          />

          <Input
            label="Access Token"
            type="password"
            value={domo.accessToken}
            onChange={(e) => setDomo({ ...domo, accessToken: e.target.value })}
          />

          <Button onClick={saveDomo} disabled={loading}>
            {loading ? 'Saving...' : 'Save Domo'}
          </Button>
        </Card>
      )}

      {/* PBI */}
      {activeTab === 'pbi' && (
        <Card>
          <h3>Power BI Credentials</h3>

          <Input
            label="Tenant ID"
            value={pbi.tenantId}
            onChange={(e) => setPbi({ ...pbi, tenantId: e.target.value })}
          />

          <Input
            label="Client ID"
            value={pbi.clientId}
            onChange={(e) => setPbi({ ...pbi, clientId: e.target.value })}
          />

          <Input
            label="Client Secret"
            type="password"
            value={pbi.clientSecret}
            onChange={(e) => setPbi({ ...pbi, clientSecret: e.target.value })}
          />

          <Button onClick={savePbi} disabled={loading}>
            {loading ? 'Saving...' : 'Save Power BI'}
          </Button>
        </Card>
      )}
    </div>
  );
}

// ---------------- INPUT COMPONENT ----------------
function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--text-secondary)' }}>
        {label}
      </div>
      <input
        {...props}
        style={{
          width: '100%',
          padding: 8,
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 13,
        }}
      />
    </div>
  );
}